import { useEffect, useRef, useState, type FC, type PropsWithChildren } from "react";

import { useTranslation } from "react-i18next";
import magicfeedback from "@magicfeedback/native";
import { getIsProductionEnvironment } from "@utils";
import { MagicFeedbackContext } from "./MagicFeedbackContext";
import { getIsSurveyDismissed, markSurveyDismissed } from "./MagicFeedbackProvider.utilities";

import type { IMagicFeedbackContextValue, SurveyPosition } from "./MagicFeedbackProvider.types";

import "./MagicFeedbackProvider.styles.css";

const SURVEY_CONTAINER_ID = "magicfeedback-survey-container";
const DEFAULT_SURVEY_POSITION: SurveyPosition = "bottom-left";
const MAGICFEEDBACK_PUBLIC_KEY = import.meta.env.VITE_MAGICFEEDBACK_PUBLIC_KEY;

const MagicFeedbackProvider: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const isMagicFeedbackInitialized = useRef(false);
  const containerReference = useRef<HTMLDivElement | null>(null);
  const [activeFormId, setActiveFormId] = useState<string | null>(null);
  const [activePosition, setActivePosition] = useState<SurveyPosition>(DEFAULT_SURVEY_POSITION);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isMagicFeedbackInitialized.current) {
      return;
    }
    if (!getIsProductionEnvironment()) {
      return;
    }

    magicfeedback.init();
    isMagicFeedbackInitialized.current = true;
    setIsInitialized(true);
  }, []);

  const hideSurvey = () => {
    setActiveFormId(null);
    if (containerReference.current != null) {
      containerReference.current.innerHTML = "";
    }
  };

  const dismissSurvey = () => {
    setActiveFormId((current) => {
      if (current != null) {
        markSurveyDismissed(current);
      }
      return null;
    });
    if (containerReference.current != null) {
      containerReference.current.innerHTML = "";
    }
  };

  const showSurvey = (formId: string, position: SurveyPosition = DEFAULT_SURVEY_POSITION) => {
    if (!MAGICFEEDBACK_PUBLIC_KEY) {
      return;
    }

    if (getIsSurveyDismissed(formId)) {
      return;
    }
    setActivePosition(position);
    setActiveFormId(formId);
  };

  useEffect(() => {
    if (activeFormId == null) {
      return;
    }
    if (!isMagicFeedbackInitialized.current) {
      return;
    }
    if (containerReference.current == null) {
      return;
    }

    containerReference.current.innerHTML = "";
    // The SDK's form(appId, publicKey) is misleadingly named: the first
    // argument identifies the form, and the second is the shared API key.
    const form = magicfeedback.form(activeFormId, MAGICFEEDBACK_PUBLIC_KEY);
    form.generate(SURVEY_CONTAINER_ID, {
      addButton: false,
      successMessage: t("onboarding.survey.button.successMessage"),
      onFinishEvent: () => {
        markSurveyDismissed(activeFormId);
      },
    });
  }, [activeFormId, t]);

  const contextValue: IMagicFeedbackContextValue = {
    showSurvey,
    hideSurvey,
    isInitialized,
  };

  const isOpen = activeFormId != null;

  return (
    <MagicFeedbackContext.Provider value={contextValue}>
      {children}
      {isOpen && (
        <div role="dialog" aria-label="Survey" className="testaviva-magicfeedback" data-position={activePosition}>
          <button
            type="button"
            onClick={dismissSurvey}
            aria-label="Close survey"
            className="testaviva-magicfeedback__close"
          >
            ×
          </button>
          <div id={SURVEY_CONTAINER_ID} ref={containerReference} />
        </div>
      )}
    </MagicFeedbackContext.Provider>
  );
};

export default MagicFeedbackProvider;
